import { useCallback, useRef, useState, useEffect, useMemo } from "react";
import { Button, Nav, Tab } from "react-bootstrap";
import messages from "messages";
// components
import ModalLayout from "components/modal/modal-layout/ModalLayout";
import AvailabilityPane from "./components/availability-pane/AvailabilityPane";
import SessionTypesPane from "./components/session-types-pane/SessionTypesPane";
// import WelcomeMessagePane from "./components/welcome-message-pane";
import CancellationPolicyPane from "./components/cancellation-policy-pane";
import PaymentMethodPane from "./components/payment-method-pane";
// styles
import styles from "./styles.module.scss";
import getClasses from "utils/getClasses";
import { AmplitudePageName } from "utils/amplitude";

export const STEPS = Object.freeze({
  createASessionType: {
    id: "createASessionType",
    label: messages.createASessionType,
  },
  setUpAvailability: {
    id: "setUpAvailability",
    label: messages.setUpAvailability,
  },
  // customizeWelcomeMessage: {
  //   id: "customizeWelcomeMessage",
  //   label: messages.customizeWelcomeMessage,
  // },
  cancellationPolicy: {
    id: "cancellationPolicy",
    label: messages.cancellationPolicy,
  },
  setUpPaymentMethod: {
    id: "setUpPaymentMethod",
    label: messages.setUpPaymentMethod,
  },
} as const);

const ORDERS = Object.keys(STEPS);

type Props = {
  show: boolean;
  onHide: () => void;
  amplitudePageName: AmplitudePageName;
};

export type PaneProps = {
  onShowModal: (arg: boolean) => void;
  onDisabled?: (arg: boolean) => void;
  error?: Error;
  className?: string;
  amplitudePageName: AmplitudePageName;
};

export type PaneRefProps = null | {
  handleApply: () => Promise<void>;
};

/**
 * renders a main setup modal for first time user
 */
const MainSetup = ({ show, onHide, amplitudePageName }: Props) => {
  const [step, setStep] = useState(0);
  const [disabled, setDisabled] = useState(false);

  useEffect(() => {
    setDisabled(false);
  }, [step]);

  const [hideBackdrop, setHideBackdrop] = useState(false);
  const [busy, setBusy] = useState(false);

  const [error, setError] = useState<any>();

  const paneRefs = useRef<Record<keyof typeof STEPS, PaneRefProps>>(
    Object.keys(STEPS).reduce((prev, curr) => {
      prev[curr] = null;
      return prev;
    }, {} as Record<keyof typeof STEPS, PaneRefProps>),
  );

  const handleBack = useCallback(
    () => setStep((s) => (s >= 1 ? s - 1 : s)),
    [],
  );

  const handleNext = useCallback(async () => {
    setBusy(true);
    setError(undefined);
    try {
      await paneRefs.current[ORDERS[step]]?.handleApply();

      if (step === 3) {
        // complete and close the modal
        onHide();
        window.location.replace("/pro-dashboard/homepage");
      } else {
        setStep((s) => s + 1);
      }
    } catch (error: any) {
      if (error.message) {
        setError(error);
      }
    } finally {
      setBusy(false);
    }
  }, [onHide, step]);

  useEffect(() => {
    setError(undefined);
  }, [step]);

  const [availabilityApplyText, setAvailabilityApplyText] = useState<string>(
    messages.next,
  );

  // only changeable in availability step / last step
  const applyText = useMemo(
    () =>
      step === Object.keys(STEPS).indexOf(STEPS.setUpAvailability.id)
        ? availabilityApplyText
        : step === ORDERS.length - 1
        ? messages.finish
        : messages.next,
    [step, availabilityApplyText],
  );

  return (
    <ModalLayout
      size={"xl"}
      className={!hideBackdrop ? "" : styles["main-setup__modal--hide"]}
      dialogClassName={styles["main-setup__dialog"]}
      bodyClass={styles["main-setup__body"]}
      footerClass={styles["main-setup__footer"]}
      show={show}
      onHide={onHide}
      backdrop={"static"}
      keyboard={false}
      Footer={
        <>
          {step > 0 && (
            <Button
              className={styles["main-setup__footer-btn"]}
              onClick={handleBack}
              variant={"outline-primary"}
              disabled={busy}
            >
              {messages.back}
            </Button>
          )}

          <Button
            className={getClasses(
              styles["main-setup__footer-btn"],
              styles["main-setup__footer-btn--next"],
            )}
            onClick={handleNext}
            disabled={busy || disabled}
          >
            {applyText}
          </Button>
        </>
      }
    >
      <Tab.Container activeKey={step}>
        <Nav className={styles["main-setup__sidebar"]}>
          {Object.entries(STEPS).map(([key, stepValue], index) => (
            <Nav.Item key={key} className={styles["main-setup__nav-item"]}>
              <Nav.Link
                eventKey={index}
                className={styles["main-setup__nav-link"]}
                disabled={index !== step}
              >
                {`${index + 1}. ${stepValue.label}`}
              </Nav.Link>
            </Nav.Item>
          ))}
        </Nav>
        <div className={styles["main-setup__tab-content-container"]}>
          <Tab.Content className={styles["main-setup__tab-content"]}>
            {ORDERS.map((key, index) => (
              <Tab.Pane eventKey={index} key={key}>
                {/* pane body */}
                {key === STEPS.createASessionType.id && (
                  <SessionTypesPane
                    ref={(el) => {
                      paneRefs.current[ORDERS[index]] = el;
                    }}
                    onShowModal={setHideBackdrop}
                    error={error}
                    amplitudePageName={amplitudePageName}
                  />
                )}
                {key === STEPS.setUpAvailability.id && (
                  <AvailabilityPane
                    ref={(el) => {
                      paneRefs.current[ORDERS[index]] = el;
                    }}
                    onShowModal={setHideBackdrop}
                    onChange={(nextSchedule: Schedule) =>
                      setAvailabilityApplyText(
                        !nextSchedule.length ? messages.skip : messages.next,
                      )
                    }
                    error={error}
                    amplitudePageName={amplitudePageName}
                  />
                )}
                {/* {key === STEPS.customizeWelcomeMessage.id && (
                  <WelcomeMessagePane
                    ref={(el) => {
                      paneRefs.current[ORDERS[index]] = el;
                    }}
                    onShowModal={setHideBackdrop}
                    error={error}
                  />
                )} */}

                {key === STEPS.cancellationPolicy.id && (
                  <CancellationPolicyPane
                    ref={(el) => {
                      paneRefs.current[ORDERS[index]] = el;
                    }}
                    onShowModal={setHideBackdrop}
                    onDisabled={(el) =>
                      step === ORDERS.indexOf(STEPS.cancellationPolicy.id) &&
                      setDisabled(el)
                    }
                    error={error}
                    amplitudePageName={amplitudePageName}
                  />
                )}

                {key === STEPS.setUpPaymentMethod.id && (
                  <PaymentMethodPane
                    ref={(el) => {
                      paneRefs.current[ORDERS[index]] = el;
                    }}
                    onShowModal={setHideBackdrop}
                    error={error}
                    amplitudePageName={amplitudePageName}
                  />
                )}
              </Tab.Pane>
            ))}
          </Tab.Content>
        </div>
      </Tab.Container>
    </ModalLayout>
  );
};

export default MainSetup;
